import React from 'react';
import styled from 'styled-components';
import { ReactComponent as CassetteSvgRaw } from '../img/art-assets/cassette-dark-rainbow.svg';
import { ReactComponent as FanMailSvgRaw } from '../img/art-assets/fanmail.svg';

interface BadgeConfig {
  label: string;
  description: string;
}

var BADGE_CONFIG: Record<string, BadgeConfig> = {
  demo_tape: {
    label: 'Demo Tape',
    description: 'One of the first 100 listeners on HeavyRotation',
  },
  test_pressing: {
    label: 'Test Pressing',
    description: 'One of the first 250 listeners on HeavyRotation',
  },
  white_label: {
    label: 'White Label',
    description: 'One of the first 500 listeners on HeavyRotation',
  },
  certified_gold: {
    label: 'Certified Gold',
    description: 'One of the first 1,000 listeners on HeavyRotation',
  },
  platinum_record: {
    label: 'Platinum Record',
    description: 'One of the first 2,500 listeners on HeavyRotation',
  },
  fan_mail: {
    label: 'Fan Mail',
    description: 'Brought a friend!',
  },
  fan_club: {
    label: 'Fan Club',
    description: 'Referred 10 friends to HeavyRotation',
  },
};

// --- Vinyl disc rendering (used by test_pressing, white_label, certified_gold, platinum_record) ---

interface VinylTheme {
  disc: [string, string, string];
  labelBg: string;
  border: string;
  tag: string;
  grooves: number[];
}

var VINYL_THEMES: Record<string, VinylTheme> = {
  test_pressing: {
    disc: ['#1a1a1a', '#0d0d0d', '#222'],
    labelBg: '#f5f0e8',
    border: '#333',
    tag: 'INSIDER',
    grooves: [0.42, 0.55, 0.67, 0.78, 0.87],
  },
  white_label: {
    disc: ['#e8e4de', '#f5f2ed', '#d0ccc5'],
    labelBg: '#ffffff',
    border: '#ccc',
    tag: 'PROMO',
    grooves: [0.4, 0.53, 0.65, 0.76, 0.86],
  },
  certified_gold: {
    disc: ['#a08520', '#c9a227', '#7a6018'],
    labelBg: '#f5f0e8',
    border: '#c9a227',
    tag: 'GOLD',
    grooves: [0.38, 0.52, 0.64, 0.77, 0.88],
  },
  platinum_record: {
    disc: ['#8a90a0', '#a0a8b8', '#686e80'],
    labelBg: '#f5f0e8',
    border: '#8890a0',
    tag: 'PLATINUM',
    grooves: [0.41, 0.54, 0.66, 0.79, 0.87],
  },
};

var SpinWrapper = styled.span<{ $spinning: boolean; $size: number }>`
  display: inline-block;
  width: ${function (p: any) {
    return p.$size + 'px';
  }};
  height: ${function (p: any) {
    return p.$size + 'px';
  }};
  ${function (p: any) {
    if (!p.$spinning) return '';
    return 'animation: wonky-spin 1.818s linear infinite; transform-origin: calc(50% + 0.4px) calc(50% + 0.4px);';
  }}

  @keyframes wonky-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

var DiscContainer = styled.span<{ $size: number }>`
  display: inline-block;
  position: relative;
  width: ${function (p: any) {
    return p.$size + 'px';
  }};
  height: ${function (p: any) {
    return p.$size + 'px';
  }};
`;

var LightOverlay = styled.span<{ $size: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${function (p: any) {
    return p.$size + 'px';
  }};
  height: ${function (p: any) {
    return p.$size + 'px';
  }};
  pointer-events: none;
`;

function VinylDiscSvg({ theme, size }: { theme: VinylTheme; size: number }) {
  var cx = size / 2;
  var cy = size / 2;
  var r = size / 2 - 1;
  var grooves = theme.grooves.map(function (ratio) {
    return r * ratio;
  });
  var id = 'bd-' + theme.tag.toLowerCase() + '-' + size;

  return (
    <svg
      width={size}
      height={size}
      viewBox={'0 0 ' + size + ' ' + size}
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <radialGradient id={'disc-' + id} cx="40%" cy="35%" r="70%">
          <stop offset="0%" stopColor={theme.disc[1]} />
          <stop offset="50%" stopColor={theme.disc[0]} />
          <stop offset="100%" stopColor={theme.disc[2]} />
        </radialGradient>
        <clipPath id={'clip-' + id}>
          <circle cx={cx} cy={cy} r={r} />
        </clipPath>
      </defs>

      <circle cx={cx} cy={cy} r={r} fill={'url(#disc-' + id + ')'} />
      {grooves.map(function (gr, i) {
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={gr}
            fill="none"
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="0.5"
          />
        );
      })}
      <circle cx={cx} cy={cy} r={r * 0.28} fill={theme.labelBg} />
      <circle
        cx={cx}
        cy={cy}
        r={r * 0.28}
        fill="none"
        stroke={theme.border}
        strokeWidth="0.5"
        opacity="0.4"
      />
      <circle cx={cx} cy={cy} r={r * 0.05} fill={theme.disc[2]} />
      <circle cx={cx} cy={cy} r={r * 0.03} fill={theme.disc[0]} />
    </svg>
  );
}

function LightingSvg({ theme, size }: { theme: VinylTheme; size: number }) {
  var cx = size / 2;
  var cy = size / 2;
  var r = size / 2 - 1;
  var id = 'lt-' + theme.tag.toLowerCase() + '-' + size;

  return (
    <svg
      width={size}
      height={size}
      viewBox={'0 0 ' + size + ' ' + size}
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={'light-' + id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="white" stopOpacity="0" />
          <stop offset="30%" stopColor="white" stopOpacity="0" />
          <stop offset="42%" stopColor="white" stopOpacity="0.12" />
          <stop offset="48%" stopColor="white" stopOpacity="0.18" />
          <stop offset="52%" stopColor="white" stopOpacity="0.12" />
          <stop offset="62%" stopColor="white" stopOpacity="0" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <clipPath id={'lclip-' + id}>
          <circle cx={cx} cy={cy} r={r} />
        </clipPath>
      </defs>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={'url(#light-' + id + ')'}
        clipPath={'url(#lclip-' + id + ')'}
      />
    </svg>
  );
}

function VinylDisc({
  theme,
  size,
  spinning,
}: {
  theme: VinylTheme;
  size: number;
  spinning?: boolean;
}) {
  return (
    <DiscContainer $size={size}>
      <SpinWrapper $spinning={!!spinning} $size={size}>
        <VinylDiscSvg theme={theme} size={size} />
      </SpinWrapper>
      <LightOverlay $size={size}>
        <LightingSvg theme={theme} size={size} />
      </LightOverlay>
    </DiscContainer>
  );
}

// --- Cassette rendering (used by demo_tape) ---

function CassetteTapeSvg({ size }: { size: number }) {
  return (
    <span style={{ display: 'inline-block', width: size, height: size }}>
      <CassetteSvgRaw width="100%" height="100%" style={{ display: 'block' }} />
    </span>
  );
}

// --- Fan Mail rendering (used by fan_mail) ---

function FanMailSvg({ size }: { size: number }) {
  return (
    <span style={{ display: 'inline-block', width: size, height: size }}>
      <FanMailSvgRaw width="100%" height="100%" style={{ display: 'block' }} />
    </span>
  );
}

// --- Badge icon dispatcher ---

function BadgeIcon({ badgeKey, size }: { badgeKey: string; size: number }) {
  if (badgeKey === 'demo_tape') {
    return <CassetteTapeSvg size={size} />;
  }
  if (badgeKey === 'fan_mail' || badgeKey === 'fan_club') {
    return <FanMailSvg size={size} />;
  }
  var vinyl = VINYL_THEMES[badgeKey];
  if (!vinyl) return null;
  return <VinylDisc theme={vinyl} size={size} spinning={badgeKey === 'test_pressing'} />;
}

// --- Badge card display ---

var BadgeCard = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
  padding: 0.6em 0.75em;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 10px;
  cursor: default;
  line-height: 1;
`;

var BadgeLabel = styled.span`
  font-size: 0.7em;
  font-weight: 600;
  color: #666;
  letter-spacing: 0.3px;
  text-align: center;
`;

interface UserBadgeProps {
  badges?: string[];
  size?: 'small' | 'medium';
  showLabel?: boolean;
}

function UserBadge(props: UserBadgeProps) {
  if (!props.badges || props.badges.length === 0) return null;

  var discSize = props.size === 'medium' ? 36 : 16;

  return (
    <>
      {props.badges.map(function (badge) {
        var config = BADGE_CONFIG[badge];
        if (!config) return null;
        return (
          <BadgeCard key={badge} title={config.description}>
            <BadgeIcon badgeKey={badge} size={discSize} />
            {props.showLabel && <BadgeLabel>{config.label}</BadgeLabel>}
          </BadgeCard>
        );
      })}
    </>
  );
}

// --- Special avatar for @heavyrotation ---

var SPECIAL_AVATAR_USERNAME = 'heavyrotation';

function isSpecialAvatarUser(username: string): boolean {
  return username.toLowerCase() === SPECIAL_AVATAR_USERNAME;
}

function SpecialAvatar({ size }: { size: number }) {
  return <VinylDisc theme={VINYL_THEMES.test_pressing} size={size} spinning />;
}

export default UserBadge;
export { BADGE_CONFIG, BadgeIcon, VinylDisc, VINYL_THEMES, isSpecialAvatarUser, SpecialAvatar };
